/**
 * Public zip-writing API for export pipelines.
 *
 * `createZipBlob` runs the archive build in a Web Worker so the main thread
 * (and the render loop) stay untouched; environments without workers
 * (Node tests, exotic embedders) fall back to a synchronous main-thread
 * build. Progress callbacks fire per completed entry either way.
 */

import { buildZipSync, type ZipEntry, type ZipProgress, type ZipProgressCallback } from './zipCore';
import { NATIVE_IMAGE_EXTENSIONS, VIDEO_EXTENSIONS } from '../../utils/AssetTypes';
import { Logger } from '../../utils/Logger';

export type { ZipEntry, ZipProgress, ZipProgressCallback };
export type { ZipLevel } from './zipCore';

/**
 * File extensions whose content is already compressed — re-DEFLATE-ing them
 * costs CPU for ~no size win, so they're STOREd. Derived from the canonical
 * AssetTypes sets where those exist; audio is listed explicitly because the
 * canonical AUDIO_EXTENSIONS includes `wav` (raw PCM), which does compress.
 */
const STORED_EXTENSIONS = new Set([
    ...NATIVE_IMAGE_EXTENSIONS, // jpg, jpeg, png, webp
    ...VIDEO_EXTENSIONS, // mp4, webm
    'm4v', 'mov', 'gif', 'avif', 'ktx', 'ktx2', 'basis',
    'mp3', 'ogg', 'opus', 'aac', 'm4a',
]);

/**
 * Compression level policy for bundled assets: STORE media that's already
 * compressed, DEFLATE everything else (JSON, glXF, GLB — GLB geometry
 * buffers compress well: measured 43MB → 32MB on a real scene).
 */
export function deflateLevelForPath(path: string): 0 | 6 {
    const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase();
    return STORED_EXTENSIONS.has(ext) ? 0 : 6;
}

type WorkerMessage =
    | ({ type: 'progress' } & ZipProgress)
    | { type: 'done'; buffer: ArrayBuffer }
    | { type: 'error'; message: string };

function tryCreateWorker(): Worker | null {
    if (typeof Worker !== 'function') return null;
    try {
        return new Worker(new URL('./zip.worker.ts', import.meta.url), { type: 'module' });
    } catch {
        return null;
    }
}

function zipInWorker(
    worker: Worker,
    entries: ZipEntry[],
    onProgress?: ZipProgressCallback,
): Promise<Uint8Array> {
    return new Promise<Uint8Array>((resolve, reject) => {
        worker.onmessage = (event: MessageEvent) => {
            const msg = event.data as WorkerMessage;
            if (msg.type === 'progress') {
                onProgress?.(msg);
            } else if (msg.type === 'done') {
                resolve(new Uint8Array(msg.buffer));
            } else {
                reject(new Error(msg.message));
            }
        };
        // Fires when the worker script itself fails to load/boot (e.g. a
        // bundler that didn't emit the worker chunk) — caller falls back.
        worker.onerror = (event: ErrorEvent) => {
            reject(new Error(event.message || 'zip worker failed to start'));
        };
        // Structured clone (no transfer): keeps `entries` usable for the
        // sync fallback if the worker never boots.
        worker.postMessage({ entries });
    });
}

/**
 * Build a zip archive from the given entries and return the raw bytes.
 *
 * Runs in a Web Worker when available; falls back to a synchronous
 * main-thread build otherwise. `onProgress` fires after each entry.
 */
export async function createZipData(
    entries: ZipEntry[],
    onProgress?: ZipProgressCallback,
): Promise<Uint8Array> {
    const worker = tryCreateWorker();
    if (worker) {
        try {
            return await zipInWorker(worker, entries, onProgress);
        } catch (error) {
            // Fall through to the sync path (worker failed to boot or died).
            // Surface it — a silent fallback blocks the main thread for the
            // whole build and would otherwise be indistinguishable from the
            // worker path working.
            Logger.warn('[zipWriter] zip worker failed, falling back to main-thread build:', error);
        } finally {
            worker.terminate();
        }
    }
    return buildZipSync(entries, onProgress);
}

/** {@link createZipData}, wrapped in a Blob for download/upload callers. */
export async function createZipBlob(
    entries: ZipEntry[],
    onProgress?: ZipProgressCallback,
): Promise<Blob> {
    const data = await createZipData(entries, onProgress);
    // The zip builders allocate output exactly, so the backing buffer is a
    // plain, exact-length ArrayBuffer (cast narrows from ArrayBufferLike).
    return new Blob([data.buffer as ArrayBuffer], { type: 'application/zip' });
}
